'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronRight } from 'lucide-react';
import { Buttons } from '../../../../components/ui/button';
import { useColor } from '../../../../components/ui/ColorContext';
import { CustomCoinIcon } from '../../../../components/ui/CustomIcons';

/* -------------------------------- utils -------------------------------- */

const hexToRgb = (hex: string): string => {
  const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!res) return '0,255,187';
  return `${parseInt(res[1], 16)}, ${parseInt(res[2], 16)}, ${parseInt(res[3], 16)}`;
};

const getThemeGradient = (type: 'basic' | 'complex') =>
  type === 'basic'
    ? 'linear-gradient(90deg,#38bdf8,#22d3ee)'
    : 'linear-gradient(90deg,#a855f7,#22c55e)';

/* ------------------------------ props ------------------------------ */

interface ScenarioCompletionProps {
  score: number;
  pointsEarned: number;
  stars: number;
  competencyLevel: number;
  newCompetencyLevel: number;
  scenarioType: 'basic' | 'complex';
  onReplay?: () => void;
  onContinue?: () => void;
  minPassingScore: number;
}

/* --------------------------- main component --------------------------- */

const ScenarioCompletionScreen: React.FC<ScenarioCompletionProps> = ({
  score,
  pointsEarned,
  stars,
  competencyLevel,
  newCompetencyLevel,
  scenarioType,
  onReplay,
  onContinue,
  minPassingScore,
}) => {
  const { primaryColor } = useColor();
  const passed = score >= minPassingScore;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] p-4">
      <div
        className="relative w-full max-w-[56rem] rounded-[2.5rem] overflow-hidden border border-white/15"
        style={{
          background: `radial-gradient(circle at top, rgba(${hexToRgb(primaryColor)},0.18), rgba(0,0,0,0.95))`,
          boxShadow: `0 0 80px rgba(${hexToRgb(primaryColor)},0.25)`,
        }}
      >
        {/* HUD GRID */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
          }}
        />

        <div className="relative z-10 p-10 space-y-10">

          {/* TITLE */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2
              className="text-4xl font-extrabold uppercase tracking-[0.3em]"
              style={{
                color: '#fff',
                textShadow: `0 0 25px rgba(${hexToRgb(primaryColor)},0.6)`,
              }}
            >
              Scenario Completion
            </h2>

            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              {passed
                ? 'Outstanding neural empathy alignment achieved.'
                : 'Neural pathways detected. Further calibration recommended.'}
            </p>
          </motion.div>

          {/* STARS */}
          <motion.div
            className="flex justify-center gap-8"
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: { staggerChildren: 0.15 },
              },
            }}
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                variants={{
                  hidden: { scale: 0, opacity: 0 },
                  visible: { scale: 1, opacity: 1 },
                }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Star
                  className={`h-24 w-24 ${
                    i < stars ? 'text-emerald-400' : 'text-slate-600'
                  }`}
                  fill={i < stars ? 'currentColor' : 'none'}
                  style={{
                    filter:
                      i < stars
                        ? 'drop-shadow(0 0 30px rgba(52,211,153,0.9))'
                        : 'none',
                  }}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* SCORE + XP */}
          <motion.div
            className="grid grid-cols-2 gap-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {/* SCORE */}
            <div className="rounded-2xl p-6 text-center border border-white/15 bg-black/60">
              <p className="text-xs uppercase tracking-widest text-slate-400">
                Score
              </p>
              <p className="mt-2 text-5xl font-bold text-white">{score}%</p>
            </div>

            {/* XP */}
            <div
              className="rounded-2xl p-6 text-center border border-white/15"
              style={{
                background: getThemeGradient(scenarioType),
                boxShadow: `0 0 40px rgba(${hexToRgb(primaryColor)},0.5)`,
              }}
            >
              <div className="flex justify-center mb-2">
                <CustomCoinIcon  color={primaryColor} className="h-14 w-14 text-white" />
              </div>
              <p className="text-xl font-bold text-white">
                +{pointsEarned} XP
              </p>
            </div>
          </motion.div>

          {/* COMPETENCY METER */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex justify-between mb-2 text-xs uppercase tracking-widest text-cyan-400">
              <span>Neural Competency Sync</span>
              <span>{competencyLevel}% → {newCompetencyLevel}%</span>
            </div>

            <div className="relative h-10 rounded-full bg-slate-900 overflow-hidden border border-white/15">
              <motion.div
                className="absolute inset-y-1 left-1 rounded-full"
                initial={{ width: `${competencyLevel}%` }}
                animate={{ width: `${newCompetencyLevel}%` }}
                transition={{ duration: 1.2 }}
                style={{
                  background: getThemeGradient(scenarioType),
                  boxShadow: '0 0 20px rgba(34,197,94,0.6)',
                }}
              />
            </div>

            <p className="mt-2 text-[11px] text-slate-400 font-mono">
              +{newCompetencyLevel - competencyLevel}% neural alignment
            </p>
          </motion.div>

          {/* ACTIONS */}
          <div className="flex items-center gap-4 pt-6">
            {score < 100 && (
              <Buttons
                variant="outline"
                className="h-14 rounded-3xl flex-1 uppercase font-bold"
                onClick={onReplay}
              >
                🔄 Replay Scenario
              </Buttons>
            )}

            <Buttons
              className="h-14 w-14 rounded-full"
              onClick={onContinue}
            >
              <ChevronRight />
            </Buttons>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioCompletionScreen;
